import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

const FacetWrap = styled("div")(({ theme }) => ({
  padding: theme.spacing(1.25, 1.75),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const FacetLabel = styled(Typography)({
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.3,
});

const facetInputSx = {
  mt: 0.75,
  mb: 0.5,
  "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8125rem" },
};

const Row = styled("div", { shouldForwardProp: (p) => p !== "checked" })(
  ({ theme, checked }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.125, 0.5),
    borderRadius: 4,
    cursor: "pointer",
    color: checked ? theme.palette.text.primary : theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  })
);

const RowLabel = styled("span")({
  flex: 1,
  minWidth: 0,
  fontSize: "0.8125rem",
  lineHeight: 1.4,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const RowCount = styled("span")(({ theme }) => ({
  fontSize: "0.6875rem",
  color: theme.palette.text.disabled,
  flexShrink: 0,
}));

function MuiFacetView({
  label,
  onMoreClick,
  onRemove,
  onSelect,
  options = [],
  showMore,
  values,
  showSearch,
  onSearch,
  searchPlaceholder,
}) {
  if (!options.length && !showSearch) return null;
  const searchVisible = showSearch;

  const toggle = (value, isSelected) => {
    if (isSelected && onRemove) onRemove(value);
    else if (onSelect) onSelect(value);
  };

  return (
    <FacetWrap>
      <FacetLabel component="h3">{label}</FacetLabel>
      {searchVisible && (
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder={searchPlaceholder || "Filter"}
          onChange={(e) => onSearch && onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={facetInputSx}
        />
      )}
      <div>
        {options.map((option, i) => {
          const checked =
            !!option.selected ||
            (Array.isArray(values) && values.includes(option.value));
          const rawValue = option.value;
          let displayLabel;
          if (rawValue && typeof rawValue === "object") {
            displayLabel =
              rawValue.name ||
              [rawValue.from, rawValue.to].filter(Boolean).join(" – ") ||
              JSON.stringify(rawValue);
          } else {
            displayLabel = String(rawValue);
          }
          return (
            <Row
              key={displayLabel || i}
              checked={checked}
              onClick={() => toggle(rawValue, checked)}
            >
              <Checkbox
                size="small"
                checked={checked}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
              />
              <RowLabel title={displayLabel}>{displayLabel}</RowLabel>
              <RowCount>{option.count}</RowCount>
            </Row>
          );
        })}
      </div>
      {showMore && (
        <Button
          size="small"
          onClick={onMoreClick}
          sx={{
            mt: 0.5,
            fontSize: "0.7rem",
            textTransform: "none",
            minWidth: 0,
            px: 0.5,
          }}
        >
          + Show more
        </Button>
      )}
    </FacetWrap>
  );
}

MuiFacetView.propTypes = {
  label: PropTypes.string,
  onMoreClick: PropTypes.func,
  onRemove: PropTypes.func,
  onSelect: PropTypes.func,
  options: PropTypes.array,
  showMore: PropTypes.bool,
  values: PropTypes.array,
  showSearch: PropTypes.bool,
  onSearch: PropTypes.func,
  searchPlaceholder: PropTypes.string,
};

export default MuiFacetView;
